import { Divider } from "antd";
import "antd/dist/reset.css";
import { useContext, useEffect, useState, useMemo } from "react";
import Draggable from "react-draggable";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Error from "../../../components/Error";
import Room from "./molecules/room";
import { BookingSideBarContent } from "./molecules/bookingSideBarContent";
import { FilterRooms } from "./molecules/filterRooms";
import { LanguageAndCurrency } from "./molecules/languageAndCurrency";
import { BookingSidebarHeader } from "./molecules/bookingSidebarHeader";

import { useTranslation } from "react-i18next";
import { CurrencyContext } from "../../../components/CurrencyContext";
import "../../../styles/Homescreen.css";
import { calculateTotalDays } from "../../../utils/calculateTotalDays";
import { calculateGrandTotal } from "./modules/calculateGrandTotal";
import { getAvailableRooms } from "./modules/getAvailableRooms";
import { getNewBookingSidebar } from "./modules/getNewBookingSidebar";
import { useBookingSideBar } from "./modules/useBookingSideBar";
import { useDateRange } from "./modules/useDateRange";
import { useFetchData } from "./modules/useFetchData";
import { useRoomsAndRatePlans } from "./modules/useRoomsAndRatePlans";
import { useCurrencyAndLanguage } from "./modules/useCurrencyAndLanguage";

function HomeTemplate() {
  const location = useLocation();
  const { isReset } = location.state || {};
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { t, i18n } = useTranslation();
  const { currency, setCurrency, convertAmount } = useContext(CurrencyContext);

  const navigate = useNavigate();
  const {
    rooms,
    loading,
    error,
    duplicaterooms,
    ratePlans,
    showSidebar,
    setShowSidebar,
    setRooms,
  } = useRoomsAndRatePlans();

  const { promotions, extraBedPrice, addonservices, branding, childrenPolicy } =
    useFetchData();
  const {
    promoDiscount,
    fromdate,
    todate,
    adultsFilter,
    childrenFilter,
    setAdultsFilter,
    setChildrenFilter,
    setPromoCode,
    promoCode,
    dateRange,
    handleDateRangeChange,
    handleApplyPromoAndFilter,
  } = useDateRange({
    duplicaterooms,
    ratePlans,
    setRooms,
  });

  const {
    expandedRooms,
    bookingSidebar,
    handlePickAddOnService,
    handleRemoveAddOnService,
    handlePickRatePlan,
    handleRoomChange,
    handleGuestInput,
    toggleExtraBed,
    handleRemoveIndividualRoom,
    handleRemoveRoomType,
    handleCloseSidebar,
    toggleRoomExpand,
    setBookingSidebar,
  } = useBookingSideBar({
    adultsFilter,
    childrenFilter,
    rooms,
    fromdate,
    todate,
    setShowSidebar,
  });

  useEffect(() => {
    if (adultsFilter || childrenFilter) {
      setBookingSidebar((prev) => {
        return prev.map((plan) => {
          return {
            ...plan,
            selectedAdults: { 0: adultsFilter },
            selectedChildren: { 0: childrenFilter },
          };
        });
      });
    }
  }, [adultsFilter, childrenFilter, setBookingSidebar]);

  useEffect(() => {
    if (isReset) {
      handleCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReset]);

  const totaldays = useMemo(
    () => calculateTotalDays(fromdate, todate),
    [fromdate, todate]
  );

  const { language, handleCurrencyChange, handleLanguageChange } =
    useCurrencyAndLanguage({
      i18n,
      setCurrency,
    });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleContinueToCheckout = () => {
    const hasInvalidRooms = bookingSidebar.some((plan) => {
      return (
        plan.type !== "addonservice" &&
        (plan.selectedRooms === 0 || !plan.selectedRooms)
      );
    });

    if (hasInvalidRooms) {
      Swal.fire({
        title: "Invalid Selection",
        text: "Please pick at least one room before proceeding to checkout.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    navigate("/booking", {
      state: {
        bookingSidebar: getNewBookingSidebar({
          bookingSidebar,
          rooms,
          ratePlans,
          fromdate,
          todate,
          extraBedPrice,
          promoDiscount,
          promoCode,
          totaldays,
        }),
        totalAmount: calculateGrandTotal({
          bookingSidebar,
          extraBedPrice,
          totaldays,
        }), // Pass the correct grand total
        childrenAgeRule: childrenPolicy.childrenAgeRule,
      },
    });
  };

  const toggleMinimizeSidebar = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <div
      className="background"
      style={{
        backgroundImage: `url(${branding.pageBackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="container">
        <FilterRooms
          isMobile={isMobile}
          searchBarGradient={branding.searchBarGradient}
          dateRange={dateRange}
          handleDateRangeChange={handleDateRangeChange}
          adultsFilter={adultsFilter}
          setAdultsFilter={setAdultsFilter}
          childrenFilter={childrenFilter}
          setChildrenFilter={setChildrenFilter}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          handleApplyPromoAndFilter={handleApplyPromoAndFilter}
          t={t}
          childrenPolicy={childrenPolicy}
        />
        <div className="row justify-content-center mt-3 mb-5">
          {loading ? (
            <h1>Loading .....</h1>
          ) : error ? (
            <Error />
          ) : Array.isArray(rooms) && rooms.length > 0 ? (
            rooms.map((room) => (
              <div className="col-md-9 mt-2" key={room._id}>
                <Room
                  promotions={promotions}
                  isMobile={isMobile}
                  room={room}
                  fromdate={fromdate}
                  todate={todate}
                  // ratePlans={ratePlans[room._id] || []}
                  ratePlans={room.ratePlans}
                  handlePickRatePlan={handlePickRatePlan}
                  getAvailableRooms={getAvailableRooms}
                  handlePickAddOnService={handlePickAddOnService}
                  addonservices={addonservices}
                  promoDiscount={promoDiscount}
                  toggleRoomExpand={toggleRoomExpand}
                  expandedRooms={expandedRooms}
                />
              </div>
            ))
          ) : (
            <div className="filter-no-results-wrapper">
              No rooms available. Please adjust your filters.
            </div>
          )}
        </div>
        <>
          {showSidebar && bookingSidebar.length > 0 && (
            <Draggable handle=".drag-handle" cancel=".no-drag">
              <div
                className="booking-sidebar-global"
                style={{
                  background: branding.searchBarGradient,
                  ...(isMobile
                    ? {
                        width: "100%",
                        position: "fixed",
                        bottom: 0,
                        height: isMinimized ? "50px" : "80%",
                        transition: "height 0.3s ease-in-out",
                      }
                    : {
                        width: "320px",
                      }),
                }}
              >
                <BookingSidebarHeader
                  isMobile={isMobile}
                  branding={branding}
                  currency={currency}
                  convertAmount={convertAmount}
                  isMinimized={isMinimized}
                  toggleMinimizeSidebar={toggleMinimizeSidebar}
                  fromdate={fromdate}
                  todate={todate}
                  handleCloseSidebar={handleCloseSidebar}
                  bookingSidebar={bookingSidebar}
                  extraBedPrice={extraBedPrice}
                  totaldays={totaldays}
                />
                {!isMinimized && (
                  <>
                    {!isMobile && (
                      <div className="ms-2 mt-2 text-white">
                        <p>
                          <strong>{t("homescreen.check_in")}:</strong>{" "}
                          {fromdate}
                        </p>
                        <p>
                          <strong>{t("homescreen.check_out")}:</strong> {todate}
                        </p>
                        <p>
                          <strong>{t("homescreen.total_nights")}:</strong>{" "}
                          {totaldays}
                        </p>
                      </div>
                    )}
                    <Divider />

                    <BookingSideBarContent
                      selectedAdults={adultsFilter}
                      selectedChildren={childrenFilter}
                      fromdate={fromdate}
                      todate={todate}
                      currency={currency}
                      convertAmount={convertAmount}
                      expandedRooms={expandedRooms}
                      rooms={rooms}
                      bookingSidebar={bookingSidebar}
                      extraBedPrice={extraBedPrice}
                      roomNameColor={branding.roomNameColor}
                      handleRemoveAddOnService={handleRemoveAddOnService}
                      toggleRoomExpand={toggleRoomExpand}
                      handleRemoveRoomType={handleRemoveRoomType}
                      handleRemoveIndividualRoom={handleRemoveIndividualRoom}
                      handleGuestInput={handleGuestInput}
                      handleContinueToCheckout={handleContinueToCheckout}
                      toggleExtraBed={toggleExtraBed}
                      handleRoomChange={handleRoomChange}
                      totaldays={totaldays}
                    />
                  </>
                )}
              </div>
            </Draggable>
          )}
        </>
      </div>
      <LanguageAndCurrency
        language={language}
        handleLanguageChange={handleLanguageChange}
        currency={currency}
        handleCurrencyChange={handleCurrencyChange}
        t={t}
      />
    </div>
  );
}

export default HomeTemplate;
